import type { ListingType } from '@shopexperts/legacy-api';

/** Matches the `max:255` rule on UpdateExpertSlugRequest. */
export const EXPERT_SLUG_MAX_LENGTH = 255;

/** Same expression the API validates against. */
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * Mirrors ExpertManagerService::expertProfilePathPrefix() on the API — the
 * redirect the admin is shown here has to match the one the API will create.
 */
const LISTING_TYPE_BASE_PATHS = new Map<string, string>([
  ['freelancer', '/freelancers'],
  ['agency', '/agencies'],
  ['app_service_provider', '/app-services'],
]);

export const PUBLIC_SITE_BASE_URL =
  (import.meta.env.VITE_WEBSITE_URL as string | undefined)?.replace(/\/$/, '') || '';

export function expertProfileBasePath(listingType: ListingType | string | null | undefined): string {
  return LISTING_TYPE_BASE_PATHS.get(listingType ?? '') ?? '/freelancers';
}

export function expertProfilePath(
  listingType: ListingType | string | null | undefined,
  slug: string,
): string {
  return `${expertProfileBasePath(listingType)}/${slug}`;
}

export function expertProfileUrl(
  listingType: ListingType | string | null | undefined,
  slug: string,
): string {
  return `${PUBLIC_SITE_BASE_URL}${expertProfilePath(listingType, slug)}`;
}

/**
 * Cleans up what the admin types without fighting them mid-word: spaces and
 * underscores become hyphens, unsupported characters are dropped, and a
 * trailing hyphen survives because it is usually the middle of a word.
 */
export function normalizeSlugInput(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-{2,}/g, '-')
    .replace(/^-+/, '')
    .slice(0, EXPERT_SLUG_MAX_LENGTH);
}

/** The value actually sent to the API — normalized, with the trailing hyphen dropped. */
export function finalizeSlug(raw: string): string {
  return normalizeSlugInput(raw).replace(/-+$/, '');
}

/** Returns the message to show under the field, or null when the slug is submittable. */
export function validateExpertSlug(slug: string, currentSlug: string | null | undefined): string | null {
  if (!slug) return 'Enter a URL slug.';
  if (slug.length > EXPERT_SLUG_MAX_LENGTH) {
    return `Use ${EXPERT_SLUG_MAX_LENGTH} characters or fewer.`;
  }
  if (!SLUG_PATTERN.test(slug)) {
    return 'Use lowercase letters, numbers, and hyphens only — no hyphen at the start or end.';
  }
  if (currentSlug && slug === currentSlug) {
    return 'This is already the current URL. Enter a different one.';
  }
  return null;
}
